import { useCallback, useState } from 'react';
import { useTenantFeatureFlagsQuery } from '@data/tenants/useTenantFeatureFlagsQuery';

/**
 * The mobile drawer's search state (SC-1096).
 *
 * Extracted from `MobileNav` because the values move together and must be reset together — keeping
 * them here is what stops the next person resetting one of them and missing the other.
 *
 * `reset` is the load-bearing part. The drawer's `<aside>` is hidden by a transform and never
 * unmounted, so `GlobalSearch` keeps the term it holds across open/close cycles: `searchKey`
 * remounts the search, which is what actually clears that term. Without it, closing a search and
 * then tapping the burger reopened the drawer showing stale results with no navigation list in it
 * at all — the primary navigation, gone.
 */
export function useDrawerSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [searchKey, setSearchKey] = useState(0);

  // Absent flags mean search is on — the endpoint defaults the same way, so a slow or failed flags
  // request shows the field rather than silently removing the feature.
  const { data: featureFlags } = useTenantFeatureFlagsQuery();

  const reset = useCallback(() => {
    setIsSearching(false);
    setSearchKey((previous) => previous + 1);
  }, []);

  return {
    isEnabled: featureFlags?.globalSearch !== false,
    isSearching,
    setIsSearching,
    searchKey,
    reset,
  };
}
